// src/components/study/StudyOnboarding.tsx
// =====================================================================
// PROGRESSIVE ONBOARDING UI — Micro-orientações contextuais
// Identidade Graia: silencioso, elegante, não intrusivo
// =====================================================================

import React, { useState, useEffect } from 'react';
import { X, MousePointer, Star, Menu, FileText, BarChart3, CheckCircle } from 'lucide-react';
import './StudyOnboarding.css';

interface WelcomeOverlayProps {
    isVisible: boolean;
    onDismiss: (permanent: boolean) => void;
}

export const WelcomeOverlay: React.FC<WelcomeOverlayProps> = ({ isVisible, onDismiss }) => {
    const [neverShow, setNeverShow] = useState(false);
    const [animIn, setAnimIn] = useState(false);

    useEffect(() => {
        if (isVisible) {
            const t = setTimeout(() => setAnimIn(true), 50);
            return () => clearTimeout(t);
        } else {
            setAnimIn(false);
        }
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div className={`ob-overlay ${animIn ? 'ob-overlay--in' : ''}`}>
            <div className="ob-welcome-card">
                {/* Header */}
                <div className="ob-welcome-header">
                    <div className="ob-welcome-icon">
                        <CheckCircle size={28} strokeWidth={1.5} />
                    </div>
                    <h2 className="ob-welcome-title">Bem-vindo aos Flashcards GRAIA</h2>
                    <p className="ob-welcome-subtitle">Interações essenciais para avançar</p>
                </div>

                {/* Steps */}
                <div className="ob-steps">
                    <div className="ob-step">
                        <div className="ob-step-icon ob-step-icon--click">
                            <MousePointer size={16} strokeWidth={2} />
                        </div>
                        <div className="ob-step-text">
                            <span className="ob-step-label">Clique no card</span>
                            <span className="ob-step-desc">Revele a resposta</span>
                        </div>
                    </div>

                    <div className="ob-step">
                        <div className="ob-step-icon ob-step-icon--rate">
                            <Star size={16} strokeWidth={2} />
                        </div>
                        <div className="ob-step-text">
                            <span className="ob-step-label">Avalie de 1 a 5</span>
                            <span className="ob-step-desc">1 = erro · 5 = domínio</span>
                        </div>
                    </div>

                    <div className="ob-step">
                        <div className="ob-step-icon ob-step-icon--menu">
                            <Menu size={16} strokeWidth={2} />
                        </div>
                        <div className="ob-step-text">
                            <span className="ob-step-label">Menu no card</span>
                            <span className="ob-step-desc">salve, reporte ou veja referências</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="ob-welcome-footer">
                    <label className="ob-checkbox-label">
                        <input
                            type="checkbox"
                            checked={neverShow}
                            onChange={(e) => setNeverShow(e.target.checked)}
                            className="ob-checkbox"
                        />
                        <span className="ob-checkbox-text">Não exibir novamente</span>
                    </label>

                    <button
                        className="ob-btn-primary"
                        onClick={() => onDismiss(neverShow)}
                    >
                        Começar
                    </button>
                </div>
            </div>
        </div>
    );
};

// =====================================================================
// TOOLTIP CONTEXTUAL
// =====================================================================

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface ContextualTooltipProps {
    isVisible: boolean;
    step: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    position?: TooltipPosition;
    anchorSelector?: string; // CSS selector do elemento âncora
    onDismiss: (permanent: boolean) => void;
    style?: React.CSSProperties;
}

export const ContextualTooltip: React.FC<ContextualTooltipProps> = ({
    isVisible,
    step,
    title,
    description,
    icon,
    position = 'bottom',
    onDismiss,
    style,
}) => {
    const [neverShow, setNeverShow] = useState(false);
    const [animIn, setAnimIn] = useState(false);

    useEffect(() => {
        if (isVisible) {
            const t = setTimeout(() => setAnimIn(true), 50);
            return () => clearTimeout(t);
        } else {
            setAnimIn(false);
        }
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div
            className={`ob-tooltip ob-tooltip--${position} ${animIn ? 'ob-tooltip--in' : ''}`}
            style={style}
            data-step={step}
        >
            <div className="ob-tooltip-arrow" />

            <button
                className="ob-tooltip-close"
                onClick={() => onDismiss(false)}
                aria-label="Fechar dica"
            >
                <X size={12} strokeWidth={2.5} />
            </button>

            <div className="ob-tooltip-header">
                <div className="ob-tooltip-icon">{icon}</div>
                <span className="ob-tooltip-title">{title}</span>
            </div>

            <p className="ob-tooltip-desc">{description}</p>

            <div className="ob-tooltip-footer">
                <label className="ob-checkbox-label ob-checkbox-label--small">
                    <input
                        type="checkbox"
                        checked={neverShow}
                        onChange={(e) => setNeverShow(e.target.checked)}
                        className="ob-checkbox"
                    />
                    <span className="ob-checkbox-text">Não exibir mais</span>
                </label>

                <button
                    className="ob-btn-dismiss"
                    onClick={() => onDismiss(neverShow)}
                >
                    Ok
                </button>
            </div>
        </div>
    );
};

// =====================================================================
// ORQUESTRADOR: renderiza o tooltip correto baseado no step
// =====================================================================

interface StudyOnboardingProps {
    currentStep: string | null;
    isVisible: boolean;
    onDismissWelcome: (permanent: boolean) => void;
    onDismissStep: (step: any, permanent: boolean) => void;
}

export const StudyOnboarding: React.FC<StudyOnboardingProps> = ({
    currentStep,
    isVisible,
    onDismissWelcome,
    onDismissStep,
}) => {
    return (
        <>
            {/* CAMADA 1: Welcome overlay */}
            <WelcomeOverlay
                isVisible={isVisible && currentStep === 'welcome'}
                onDismiss={onDismissWelcome}
            />

            {/* CAMADA 2: Rating tooltip — aparece nos botões de avaliação */}
            <ContextualTooltip
                isVisible={isVisible && currentStep === 'rating'}
                step="rating"
                title="Autoavaliação"
                description="Defina seu nível de acerto: 1 = erro · 3 = parcial · 5 = domínio. Isso ajusta a repetição dos cards."
                icon={<Star size={14} strokeWidth={2} />}
                position="top"
                style={{
                    position: 'fixed',
                    bottom: '130px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 3000,
                }}
                onDismiss={(p) => onDismissStep('rating', p)}
            />

            {/* CAMADA 2: Menu tooltip */}
            <ContextualTooltip
                isVisible={isVisible && currentStep === 'menu'}
                step="menu"
                title="Ações do card"
                description="Salve para revisar depois, reporte inconsistências ou acesse referências."
                icon={<Menu size={14} strokeWidth={2} />}
                position="bottom"
                style={{
                    position: 'fixed',
                    top: '170px',
                    right: '80px',
                    zIndex: 3000,
                }}
                onDismiss={(p) => onDismissStep('menu', p)}
            />

            {/* CAMADA 2: Details tooltip */}
            <ContextualTooltip
                isVisible={isVisible && currentStep === 'details'}
                step="details"
                title="Conteúdo de apoio"
                description="Acesse uma explicação complementar para aprofundar o entendimento do tema."
                icon={<FileText size={14} strokeWidth={2} />}
                position="bottom"
                style={{
                    position: 'fixed',
                    top: '130px',
                    right: '180px',
                    zIndex: 3000,
                }}
                onDismiss={(p) => onDismissStep('details', p)}
            />

            {/* CAMADA 2: Sidebar tooltip */}
            <ContextualTooltip
                isVisible={isVisible && currentStep === 'sidebar'}
                step="sidebar"
                title="Estatísticas da sessão"
                description="Acompanhe progresso, precisão e distribuição das suas avaliações."
                icon={<BarChart3 size={14} strokeWidth={2} />}
                position="left"
                style={{
                    position: 'fixed',
                    top: '20px',
                    right: '60px',
                    zIndex: 3000,
                }}
                onDismiss={(p) => onDismissStep('sidebar', p)}
            />
        </>
    );
};